import { makeStyles, Typography } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faLock } from "@fortawesome/free-solid-svg-icons"
import { faClock } from "@fortawesome/free-regular-svg-icons"

import { colors } from "../../../utils/theme"
import { Enum_Safety_Plan_Type_Enum } from "../../../generated/graphql"
import { planTypeToString } from "../../../utils/safetyPlanUtils"

/**
 * Placeholder card shown to the practitioner when a client has not yet
 * completed their safety plan in the mobile app. Displays the client's name
 * with a "Connected" badge to confirm the practitioner-client link is active.
 *
 * @param name - The client's full name.
 */
export default function SafetyPlanInProgress({
  name,
  planType,
}: {
  name: string
  planType: Enum_Safety_Plan_Type_Enum | undefined
}) {
  const classes = useStyles()

  return (
    <>
      <div className={classes.container}>
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <div className={classes.headerIcon}>
              <FontAwesomeIcon icon={faLock} />
            </div>
            <Typography variant="h5" className={classes.headerTitle}>
              This Client is building their {planTypeToString(planType, true)}
            </Typography>
            <div id="clk" className={classes.headerClock}>
              <FontAwesomeIcon icon={faClock} />
            </div>
          </div>
        </div>

        <div id="united" className={classes.body}>
          <div id="section" className={classes.section}>
            <Typography id="content" className={classes.content}>
              The Client is building their {planTypeToString(planType, false)}{" "}
              now. When the {planTypeToString(planType, false)} is finished you
              will see the details here – check back soon!
            </Typography>
            <div id="contact" className={classes.contact}>
              <div id="btncontainer" className={classes.contactCard}>
                <div className={classes.contactCardContent}>
                  <Typography id="name" className={classes.contactName}>
                    {name}
                  </Typography>
                  <div id="btn" className={classes.connectedPill}>
                    <Typography id="txt" className={classes.connectedText}>
                      Connected
                    </Typography>
                    <FontAwesomeIcon
                      icon={faCheck}
                      className={classes.connectedCheckIcon}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    height: "530px",
    flexDirection: "column",
    gap: "16px",
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: "4px",
    maxWidth: "600px",
  },
  header: {
    padding: "12px 20px",
    gap: "24px",
    borderRadius: "8px 8px 0px 0px",
    borderBottom: `1px solid ${colors.safetyPlan.grayscale.black10}`,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  headerIcon: {
    display: "flex",
    width: "36px",
    height: "36px",
    padding: "5.538px 6.923px 8.308px 6.923px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "360px",
    background: colors.safetyPlan.sky,
    color: "white",
  },
  headerTitle: {
    flex: "1 0 0",
    lineHeight: "20px",
  },
  headerClock: {
    display: "flex",
    width: "16px",
    height: "16px",
    justifyContent: "center",
    alignItems: "center",
  },
  body: {
    display: "flex",
    padding: "16px 20px 24px 20px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
    alignSelf: "stretch",
  },
  section: {
    display: "flex",
    paddingBottom: "32px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "32px",
    alignSelf: "stretch",
  },
  content: {
    flex: "1 0 0",
    color: "#545E78",
    textAlign: "center",
    fontFamily: "Lato",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  contact: {
    display: "flex",
    paddingBottom: "32px",
    flexDirection: "column",
    alignItems: "center",
    gap: "32px",
    alignSelf: "stretch",
  },
  contactCard: {
    display: "flex",
    width: "350px",
    minHeight: "52px",
    padding: "12px 16px",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    borderRadius: "4px",
    background: "rgba(0, 0, 0, 0.03)",
  },
  contactCardContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
  },
  contactName: {
    color: colors.safetyPlan.midnight,
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "20px",
  },
  connectedPill: {
    display: "flex",
    padding: "8px 8px 8px 16px",
    alignItems: "center",
    gap: "4px",
    borderRadius: "360px",
    background: "#007546",
  },
  connectedText: {
    color: colors.neutral.white,
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "16px",
  },
  connectedCheckIcon: {
    color: "white",
  },
}))
